import { RootStore } from '~/stores/root.store';
import dayjs from 'dayjs';
import { featureRestrictionLogger } from '~/components/FeatureRestriction/featureRestrictionLogger';
import { isReactFirst } from '~/utils/environment.utils';
import { Account } from '~/models/account.model';
import { TAutomationsMode } from '~/types/server.types';

interface IFetchFeaturePlanOptions {
    forceFetch?: boolean;
}

export class AccountController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async fetchFeaturePlan(options?: IFetchFeaturePlanOptions) {
        const {
            userStore: { currUser },
            accountService,
            accountStore,
            accountStore: {
                isFeaturePlanLoading,
                hasLoadedFeaturePlan,
                latestFeaturePlanFetchDate
            },
            injectorService
        } = this.rootStore;

        if (isFeaturePlanLoading) {
            return;
        }

        const hasLastFetchExpired =
            dayjs().diff(latestFeaturePlanFetchDate, 'minute') >= 30;

        const shouldFetchFeaturePlan =
            !hasLoadedFeaturePlan || options?.forceFetch || hasLastFetchExpired;

        if (!shouldFetchFeaturePlan) {
            return;
        }

        accountStore.setIsFeaturePlanLoading(true);
        try {
            const account = currUser.account;
            const response = await accountService.fetchFeaturePlan(account._id);

            accountStore.setLastFeaturePlanFetchDate();
            account.setFeaturePlan(response.feature_variations);

            // Only call Angular's AccountsManager if not React first
            if (!isReactFirst()) {
                injectorService
                    .get('AccountsManager')
                    .fetchFeaturePlan(options);
            }
        } catch (error) {
            featureRestrictionLogger.trackError({
                error,
                message: 'An error occurred when fetching the feature plan',
                context: {
                    pricing_and_packaging: true
                }
            });
        } finally {
            if (!hasLoadedFeaturePlan) {
                accountStore.setHasLoadedFeaturePlan(true);
            }
            accountStore.setIsFeaturePlanLoading(false);
        }
    }
    updateAutomationsMode(account: Account, mode: TAutomationsMode) {
        const { injectorService } = this.rootStore.globalContext;
        account.updateAutomationsMode(mode);
        if (isReactFirst()) {
            return;
        }
        const currUser = injectorService?.get('UsersManager')?.currUser;
        if (currUser?.account) {
            Object.assign(currUser.account, { automations_mode: mode });
        }
    }
}
